'use client'

import { HTMLProps, useEffect, useRef } from 'react'

export interface AppleIDAuthorization {
  code: string
  id_token: string
  state?: string

  /**
   * Important: Apple only returns the user object the first time the user authorizes the app.
   */
  user?: { name?: { firstName: string; lastName: string }; email?: string }
}

function isAppleIdSignInSuccessEvent(evt: unknown): evt is CustomEvent<{ authorization: AppleIDAuthorization }> {
  return (
    evt instanceof CustomEvent &&
    evt.type === 'AppleIDSignInOnSuccess' &&
    evt.detail.authorization &&
    typeof evt.detail.authorization === 'object' &&
    evt.detail.authorization !== null &&
    'code' in evt.detail.authorization &&
    'id_token' in evt.detail.authorization
  )
}

export interface AppleLoginButtonProps extends HTMLProps<HTMLDivElement> {
  onSignIn?: (authorization: AppleIDAuthorization) => void
}

/**
 * @see https://developer.apple.com/documentation/sign_in_with_apple/configuring-your-webpage-for-sign-in-with-apple
 */
export default function AppleLoginButton(props: AppleLoginButtonProps) {
  const { onSignIn, ...otherProps } = props

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any
    const renderButton = win?.AppleID?.auth?.renderButton

    // Note: If this condition is met; AppleID SDK is already loaded and initialized;
    // meaning it will not automatically render the button on script load (because it
    // is already loaded).
    // We force a render of the button to ensure it is present on the page.
    // This might happen after a soft navigation from login to logout or vice versa.
    if (typeof renderButton === 'function') {
      renderButton()
    }
  }, [])

  // Enure we can pass a stable dependency to the useEffect below
  const onSignInRef = useRef(onSignIn)
  onSignInRef.current = onSignIn

  useEffect(() => {
    const handleSuccess = (ev: Event) => {
      if (isAppleIdSignInSuccessEvent(ev)) {
        const authorization: AppleIDAuthorization = ev.detail.authorization
        const user: unknown = authorization.user

        if (user && typeof user === 'string') {
          // Note: according to apple docs, the user object is a JSON string
          // see: https://developer.apple.com/documentation/sign_in_with_apple/configuring-your-webpage-for-sign-in-with-apple#:~:text=A%20JSON%20string%20containing%20the%20data%20requested%20in%20the%20scope%20property.%20The%20returned%20data%20is%20in%20the%20following%20format%3A
          authorization.user = JSON.parse(user)
        }

        onSignInRef.current?.(authorization)
      }
    }

    const handleFailure = console.debug.bind(undefined, 'AppleID sign-in failed')

    document.addEventListener('AppleIDSignInOnSuccess', handleSuccess)
    document.addEventListener('AppleIDSignInOnFailure', handleFailure)
    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', handleSuccess)
      document.removeEventListener('AppleIDSignInOnFailure', handleFailure)
    }
  }, [])

  return (
    <div
      {...otherProps}
      className="h-[40px] w-[320px]"
      id="appleid-signin"
      data-color="black"
      data-border="true"
      data-type="sign in"
    />
  )
}
